import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type { LngAllocationsForShipperParams } from 'src/apis/lng-marketing/types';
import { useLngAllocationsForShipper } from 'src/apis/lng-marketing/use-lng-allocations-for-shipper';
import { type LngMarketingRootRequest } from 'src/apis/lng-marketing/use-lng-marketing-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useLngAllocationsForShipperPageParams } from 'src/pages/lng/allocations/overview/use-page-params';

const Page = lazy(() => import('./page'));

export const LngAllocationsForShippers: FC<{
  lngMarketingRoot: Successful<LngMarketingRootRequest>;
}> = ({ lngMarketingRoot }) => {
  const pageParams = useLngAllocationsForShipperPageParams();

  const params = useMemo(() => {
    return {
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
      sortBy: pageParams.value.sortBy,
      sortDirection: pageParams.value.sortDirection,
      terminalId: pageParams.value.terminalId.map((terminal) => terminal.value),
      availableServices: pageParams.value.availableServices,
      lngDealId: pageParams.value.lngDealId,
      offerName: pageParams.value.offerName,
      serviceRuntimeStart: pageParams.value.serviceRuntimeStart,
      serviceRuntimeEnd: pageParams.value.serviceRuntimeEnd,
      lsoOrganisationId: pageParams.value.lsoOrganisationId.map(
        (lso) => lso.value
      ),
      mwhPriceMin: pageParams.value.mwhPriceMin ?? undefined,
      mwhPriceMax: pageParams.value.mwhPriceMax ?? undefined,
      gasAmountMin: pageParams.value.gasAmountMin ?? undefined,
      gasAmountMax: pageParams.value.gasAmountMax ?? undefined,
    } satisfies LngAllocationsForShipperParams;
  }, [pageParams.value]);

  const lngAllocations = useLngAllocationsForShipper({
    lngMarketingRoot,
    params,
  });

  if (lngAllocations.response == null) return <PageSpinner />;

  return (
    <Page
      pageParams={pageParams}
      lngAllocations={lngAllocations}
      lngMarketingRoot={lngMarketingRoot}
    />
  );
};
